import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { BookingModal } from '../components/shared/BookingModal';
import { Calendar, User, Tag, ArrowLeft, ArrowRight, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample blog post content for demo
const samplePost = {
    id: '1',
    title: 'The Complete Guide to GMB Optimization in 2024',
    slug: 'gmb-optimization-guide-2024',
    excerpt: 'Learn how to optimize your Google Business Profile to rank #1 in local searches and drive more foot traffic.',
    content: `
        <h2>Why GMB Optimization Matters</h2>
        <p>Google Business Profile (formerly Google My Business or GMB) is one of the most powerful tools for local businesses. When someone searches for services "near me" or in a specific location, your GMB profile is what appears in the map pack - those three prominent listings below the map in Google search results.</p>
        
        <p>Studies show that 46% of all Google searches have local intent, and businesses with complete GMB profiles are 70% more likely to attract local visits. That's why optimizing your GMB profile isn't optional - it's essential.</p>
        
        <h2>Step 1: Claim and Verify Your Profile</h2>
        <p>The first step is claiming your Google Business Profile. If you haven't already, go to business.google.com and either claim an existing listing or create a new one. You'll need to verify your business, typically through a postcard sent to your business address.</p>
        
        <h2>Step 2: Complete Every Section</h2>
        <p>Google rewards completeness. Fill out every available field in your profile:</p>
        <ul>
            <li>Business name (use your real business name, no keyword stuffing)</li>
            <li>Address (if you have a physical location)</li>
            <li>Service areas (if you serve customers at their location)</li>
            <li>Phone number (use a local number)</li>
            <li>Website URL</li>
            <li>Hours of operation (including special hours)</li>
            <li>Business description (up to 750 characters)</li>
            <li>Categories (choose primary and secondary categories carefully)</li>
            <li>Attributes (accessibility features, payment methods, etc.)</li>
        </ul>
        
        <h2>Step 3: Add High-Quality Photos</h2>
        <p>Businesses with photos receive 42% more requests for directions and 35% more clicks to their websites. Upload:</p>
        <ul>
            <li>Cover photo that represents your business</li>
            <li>Logo</li>
            <li>Interior and exterior photos</li>
            <li>Team photos</li>
            <li>Product or service photos</li>
        </ul>
        <p>Aim for at least 10-15 photos, and add new ones regularly.</p>
        
        <h2>Step 4: Get and Respond to Reviews</h2>
        <p>Reviews are a crucial ranking factor. Create a system to:</p>
        <ul>
            <li>Ask satisfied customers for reviews</li>
            <li>Make it easy with a direct review link</li>
            <li>Respond to ALL reviews (positive and negative)</li>
            <li>Thank positive reviewers personally</li>
            <li>Address negative reviews professionally</li>
        </ul>
        
        <h2>Step 5: Post Regular Updates</h2>
        <p>Google Posts keep your profile fresh and engaging. Post about:</p>
        <ul>
            <li>Offers and promotions</li>
            <li>Events</li>
            <li>Product updates</li>
            <li>News and announcements</li>
        </ul>
        <p>Aim for at least one post per week to show Google your profile is active.</p>
        
        <h2>Step 6: Build Citations</h2>
        <p>Citations are mentions of your business name, address, and phone number (NAP) on other websites. Ensure your NAP is consistent across:</p>
        <ul>
            <li>Your website</li>
            <li>Social media profiles</li>
            <li>Local directories (Yelp, Yellow Pages, industry-specific directories)</li>
            <li>Data aggregators</li>
        </ul>
        
        <h2>Measuring Success</h2>
        <p>Track your GMB performance through:</p>
        <ul>
            <li>Search queries that triggered your profile</li>
            <li>Profile views (Maps vs Search)</li>
            <li>Customer actions (calls, directions, website visits)</li>
            <li>Photo views compared to competitors</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>GMB optimization is an ongoing process, not a one-time task. By following these steps and consistently maintaining your profile, you'll improve your visibility in local searches and attract more customers to your business.</p>
        
        <p>Need help optimizing your Google Business Profile? <a href="/contact">Contact us</a> for a free audit and see how we can help you dominate local search.</p>
    `,
    category: 'gmb-local-seo',
    author: 'Vikram Singh',
    featured_image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&auto=format&fit=crop',
    created_at: '2024-01-15T10:00:00Z',
    tags: ['GMB', 'Local SEO', 'Google Maps'],
};

const relatedPosts = [
    {
        title: 'Local SEO Checklist: 50 Points for Rankings',
        slug: 'local-seo-checklist-50-points',
        excerpt: 'The ultimate checklist for local SEO success.',
        featured_image: 'https://images.unsplash.com/photo-1553484771-8bbd3e39ce35?w=400&auto=format&fit=crop',
    },
    {
        title: 'How to Rank in ChatGPT and AI Search Engines',
        slug: 'rank-in-chatgpt-ai-search',
        excerpt: 'Optimize your content for AI-powered search.',
        featured_image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&auto=format&fit=crop',
    },
];

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bookingOpen, setBookingOpen] = useState(false);

    useEffect(() => {
        fetchPost();
    }, [slug]);

    const fetchPost = async () => {
        try {
            const response = await axios.get(`${API}/blog/${slug}`);
            setPost(response.data);
        } catch (error) {
            console.log('Using sample post');
            setPost(samplePost);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const sharePost = () => {
        if (navigator.share) {
            navigator.share({
                title: post.title,
                text: post.excerpt,
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
                    <Button asChild className="rounded-full">
                        <Link to="/blog">Back to Blog</Link>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="relative" data-testid="blog-post-page">
            {/* Hero */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <img 
                    src={post.featured_image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 pb-12">
                    <div className="container-custom">
                        <Link 
                            to="/blog"
                            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Blog
                        </Link>
                        <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 max-w-4xl">
                            {post.title}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                {post.author}
                            </span>
                            <span className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                {formatDate(post.created_at)}
                            </span>
                            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                                {post.category?.replace(/-/g, ' ').toUpperCase()}
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Main Content */}
                        <article className="lg:col-span-8">
                            <div 
                                className="wysiwyg-content prose prose-lg max-w-none dark:prose-invert"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                            />

                            {/* Tags */}
                            {post.tags && post.tags.length > 0 && (
                                <div className="mt-12 pt-8 border-t border-border">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Tag className="w-4 h-4 text-muted-foreground" />
                                        {post.tags.map((tag, index) => (
                                            <span 
                                                key={index}
                                                className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Share */}
                            <div className="mt-8 pt-8 border-t border-border">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Share this article</span>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={sharePost}
                                        className="rounded-full"
                                    >
                                        <Share2 className="w-4 h-4 mr-2" />
                                        Share
                                    </Button>
                                </div>
                            </div>
                        </article>

                        {/* Sidebar */}
                        <aside className="lg:col-span-4">
                            <div className="lg:sticky lg:top-24 space-y-8">
                                {/* CTA Card */}
                                <div className="p-6 rounded-2xl bg-foreground text-background">
                                    <h3 className="text-xl font-semibold mb-3">
                                        Need Help with SEO?
                                    </h3>
                                    <p className="text-background/70 mb-4 text-sm">
                                        Book a free strategy call and see how we can help your business grow.
                                    </p>
                                    <Button 
                                        className="w-full bg-background text-foreground hover:bg-background/90 rounded-full"
                                        onClick={() => setBookingOpen(true)}
                                    >
                                        Book Free Call
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>

                                {/* Related Posts */}
                                <div>
                                    <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                                    <div className="space-y-4">
                                        {relatedPosts.map((relatedPost, index) => (
                                            <Link
                                                key={index}
                                                to={`/blog/${relatedPost.slug}`}
                                                className="flex gap-4 p-3 rounded-xl border border-border hover:border-primary/30 transition-colors group"
                                            >
                                                <img 
                                                    src={relatedPost.featured_image}
                                                    alt={relatedPost.title}
                                                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                                                />
                                                <div>
                                                    <h4 className="font-medium text-sm group-hover:text-primary transition-colors line-clamp-2">
                                                        {relatedPost.title}
                                                    </h4>
                                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                        {relatedPost.excerpt}
                                                    </p>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom text-center">
                    <h2 className="text-3xl font-semibold tracking-tight mb-4">
                        Want More SEO Insights?
                    </h2>
                    <p className="text-muted-foreground mb-8">
                        Subscribe to our newsletter for weekly tips and strategies.
                    </p>
                    <Button asChild className="rounded-full px-8">
                        <Link to="/blog">Explore More Articles</Link>
                    </Button>
                </div>
            </section>

            <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
        </div>
    );
}
